<template>
  <div>
    <!-- ASISTENTE FLOTANTE JEAN-BOT (PERSONAJE COMPLETO SIN CÍRCULO) -->
    <transition name="fade-slide">
      <div v-if="!abierto" class="jeanbot-floating-container">
        <!-- TARJETA DE BIENVENIDA Y PUBLICIDAD CON BOTÓN 'X' PARA QUITAR -->
        <transition name="welcome-card-anim">
          <div
            v-if="mostrarBienvenida"
            class="jeanbot-welcome-card"
            @click="abrirAsistente"
            role="button"
            tabindex="0"
            aria-label="Mensaje de bienvenida Jean Informático"
          >
            <!-- Botón 'X' para que el usuario pueda quitar el mensaje -->
            <button
              type="button"
              class="welcome-card-close"
              @click.stop="cerrarBienvenida"
              aria-label="Quitar mensaje de bienvenida"
              title="Quitar mensaje"
            >
              <i class="fas fa-times"></i>
            </button>

            <div class="welcome-card-body">
              <div class="welcome-card-header">
                <span class="welcome-wave-icon">👋</span>
                <div class="welcome-header-texts">
                  <h4 class="welcome-title">¡Bienvenido a Jean Informático C.A.!</h4>
                  <span class="welcome-badge-tag">Asesoría & Servicios</span>
                </div>
              </div>

              <p class="welcome-message">
                Recuerda que estoy aquí para <strong>servirte</strong>: consulta tus órdenes de servicio, precios de productos, soporte técnico o chatea conmigo en vivo.
              </p>

              <div class="welcome-card-footer">
                <span class="welcome-cta-btn">
                  <i class="fas fa-comment-dots"></i> ¡Haz clic aquí para ayudarte!
                </span>
              </div>
            </div>

            <!-- Flechita indicadora que apunta a Jean-Bot -->
            <div class="welcome-bubble-pointer"></div>
          </div>
        </transition>

        <!-- PERSONAJE FLOTANTE JEAN-BOT (TOTALMENTE VISIBLE Y SIN CÍRCULO) -->
        <div
          class="jeanbot-mascot-wrapper"
          @click="abrirAsistente"
          role="button"
          tabindex="0"
          aria-label="Hablar con JeanBot"
          title="¡Hola! Haz clic sobre JeanBot para chatear"
        >
          <!-- Sombra dinámica en el suelo -->
          <div class="mascot-ground-shadow"></div>

          <!-- Resplandor tecnológico suave -->
          <div class="mascot-ambient-glow"></div>

          <!-- Imagen completa y nítida de JeanBot -->
          <img
            :src="botAvatarImg"
            alt="JeanBot Asistente Jean Informático"
            class="jeanbot-mascot-img"
            @error="onBotImgError"
          />

          <!-- Pill flotante 'En línea' -->
          <div class="mascot-online-pill">
            <span class="mascot-online-ping"></span>
            <span class="mascot-online-dot"></span>
            <span class="mascot-online-label">En línea</span>
          </div>

          <!-- Badge si hay resultado o notificación -->
          <span class="mascot-status-badge" v-if="hayResultado">✓</span>
        </div>
      </div>
    </transition>

    <!-- MODAL ASISTENTE TIPO CHAT -->
    <transition name="chat-slide">
      <div
        v-if="abierto"
        class="asistente-overlay"
        @click.self="cerrarAsistente"
      >
        <div class="asistente-window">
          <!-- HEADER -->
          <div class="asistente-header">
            <div class="header-left">
              <div class="bot-avatar">
                <img
                  :src="botAvatarImg"
                  alt="JeanBot"
                  class="bot-avatar-header-img"
                  @error="onBotImgError"
                />
                <span class="online-dot"></span>
              </div>
              <div>
                <h3 class="bot-name">JeanBot</h3>
                <p class="bot-status">
                  <i class="fas fa-circle text-success mr-1" style="font-size: 6px;"></i>
                  En línea · Asistente Virtual
                </p>
              </div>
            </div>
            <div class="header-actions">
              <button
                class="header-btn"
                @click="reiniciarTodo"
                title="Nueva conversación"
              >
                <i class="fas fa-redo-alt"></i>
              </button>
              <button
                class="header-btn header-btn-close"
                @click="cerrarAsistente"
                title="Cerrar"
              >
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>

          <!-- CUERPO DEL CHAT -->
          <div class="asistente-body" ref="chatBody">
            <transition-group name="msg-in" tag="div" class="chat-messages">
              <div
                v-for="(msg, idx) in mensajes"
                :key="idx"
                :class="['msg-row', msg.tipo === 'bot' ? 'msg-bot' : 'msg-user']"
              >
                <div v-if="msg.tipo === 'bot'" class="msg-avatar-bot">
                  <img
                    :src="botAvatarImg"
                    alt="JeanBot"
                    class="msg-avatar-bot-img"
                    @error="onBotImgError"
                  />
                </div>
                <div :class="['msg-bubble', msg.tipo === 'bot' ? 'bubble-bot' : 'bubble-user']">
                  <!-- Contenido del mensaje -->
                  <div class="bubble-content" v-html="msg.texto"></div>

                  <!-- Tarjetas visuales de productos (informativas) -->
                  <div v-if="msg.productos && msg.productos.length > 0" class="msg-productos-grid mt-2">
                    <div
                      v-for="prod in msg.productos"
                      :key="prod.id"
                      class="bot-inline-card"
                    >
                      <div class="inline-card-thumb">
                        <img
                          v-if="prod.imagen"
                          :src="prod.imagen"
                          :alt="prod.nombre"
                          @error="$event.target.style.display='none'"
                        />
                        <div v-else class="inline-card-ph">
                          <i class="fas fa-box"></i>
                        </div>
                      </div>
                      <div class="inline-card-content">
                        <div class="inline-card-title">{{ prod.nombre }}</div>
                        <div class="inline-card-tag">{{ prod.categoria }}</div>
                        <div class="inline-card-prices">
                          <span class="price-usd-strong">{{ prod.precioUsdFormatted }}</span>
                          <span class="price-bs-small">≈ {{ prod.precioBsFormatted }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- OPCIONES CLICKEABLES (Quick Replies) -->
                  <div v-if="msg.opciones && msg.opciones.length > 0" class="msg-opciones-chips mt-2">
                    <button
                      v-for="opt in msg.opciones"
                      :key="opt.id"
                      class="btn-opcion-chip"
                      @click="ejecutarOpcion(opt)"
                      :disabled="botEscribiendo"
                    >
                      {{ opt.label }}
                    </button>
                  </div>

                  <div v-if="msg.hora" class="msg-hora">{{ msg.hora }}</div>
                </div>
              </div>
            </transition-group>

            <!-- INDICADOR "escribiendo..." -->
            <div v-if="botEscribiendo" class="msg-row msg-bot">
              <div class="msg-avatar-bot">
                <img
                  :src="botAvatarImg"
                  alt="JeanBot"
                  class="msg-avatar-bot-img is-typing"
                  @error="onBotImgError"
                />
              </div>
              <div class="msg-bubble bubble-bot typing-bubble">
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
                <span class="typing-dot"></span>
              </div>
            </div>
          </div>

          <!-- BARRA DE ENTRADA FIJA -->
          <div class="chat-input-area chat-input-area-sticky">
            <div class="input-chat-group">
              <i class="fas fa-comment-dots input-icon text-primary"></i>
              <input
                ref="inputMensajeIa"
                v-model="inputMensaje"
                type="text"
                class="chat-input"
                :placeholder="placeholderInput"
                :disabled="botEscribiendo || cargando"
                @keyup.enter="enviarMensajeIa"
              />
              <button
                class="btn-send"
                @click="enviarMensajeIa"
                :disabled="botEscribiendo || cargando || !inputMensaje.trim()"
                title="Enviar mensaje"
              >
                <i :class="botEscribiendo ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- MODAL POLÍTICAS -->
    <transition name="fade-slide">
      <div v-if="showModalPoliticas" class="modal-politicas-overlay" @click.self="cerrarModalPoliticas">
        <div class="modal-politicas-card">
          <div class="modal-politicas-header">
            <div class="modal-politicas-header-icon"><i class="fas fa-shield-alt"></i></div>
            <h3 class="modal-politicas-title">Políticas del Servicio</h3>
            <p class="modal-politicas-subtitle">Por favor lea y acepte antes de continuar</p>
          </div>
          <div class="modal-politicas-body">
            <div class="politica-item">
              <div class="politica-icon bg-purple-subtle text-purple"><i class="fas fa-credit-card"></i></div>
              <div>
                <h4 class="politica-titulo">Pago con Cashea</h4>
                <p class="politica-desc">
                  El pago con <strong>Cashea</strong> debe realizarse <strong>presencialmente en el local</strong> al momento de retirar el equipo. No se aceptan pagos Cashea por transferencia.<br>
                  <strong class="text-purple">Monto mínimo para Cashea: $25.</strong> Si tu total es menor, solo se acepta pago de contado.
                </p>
              </div>
            </div>
            <div class="politica-item">
              <div class="politica-icon bg-orange-subtle text-warning"><i class="fas fa-clock"></i></div>
              <div>
                <h4 class="politica-titulo">Retiro del Equipo</h4>
                <p class="politica-desc">
                  Una vez notificado que el equipo está reparado, el cliente tiene <strong>15 días hábiles</strong> para retirarlo. Pasado ese tiempo, <strong>no nos hacemos responsables</strong> del equipo.
                </p>
              </div>
            </div>
            <div class="politica-item">
              <div class="politica-icon bg-blue-subtle text-primary"><i class="fas fa-clock"></i></div>
              <div>
                <h4 class="politica-titulo">Horario de Pagos</h4>
                <p class="politica-desc">Los pagos se pueden realizar únicamente de <strong>8:30 AM a 5:30 PM</strong>.</p>
              </div>
            </div>
            <button @click="cerrarModalPoliticas" class="btn btn-consultar btn-lg btn-block py-3 font-weight-bold mt-4">
              <i class="fas fa-check-circle mr-2"></i> Acepto y Continuar al Pago
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- MODAL PAGO -->
    <transition name="fade-slide">
      <div v-if="showModalPago" class="modal-pago-overlay" @click.self="cerrarModalPago">
        <div class="modal-pago-card">
          <div class="modal-pago-header">
            <h3 class="modal-pago-title"><i class="fas fa-credit-card mr-2"></i> Realizar Pago</h3>
            <button @click="cerrarModalPago" class="modal-pago-close"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-pago-body">
            <div v-if="!bancoSeleccionado" class="bancos-grid">
              <p class="label-uppercase text-muted mb-3 text-center">Seleccione su banco</p>
              <div class="row g-3">
                <div class="col-6">
                  <button @click="seleccionarBanco('venezuela')" class="banco-btn">
                    <img src="@/assets/Img/banco/logo-venezuela.png" alt="Banco de Venezuela" class="banco-logo-img" />
                  </button>
                </div>
                <div class="col-6">
                  <button @click="seleccionarBanco('mercantil')" class="banco-btn">
                    <img src="@/assets/Img/banco/logo-mercatil.png" alt="Mercantil" class="banco-logo-img" />
                  </button>
                </div>
              </div>
            </div>
            <div v-else class="datos-banco">
              <button @click="bancoSeleccionado = null" class="btn btn-outline-secondary btn-sm mb-3">
                <i class="fas fa-arrow-left mr-1"></i> Cambiar banco
              </button>
              <div class="banco-info-card">
                <div class="d-flex align-items-center mb-3">
                  <img
                    :src="bancoSeleccionado === 'venezuela' ? require('@/assets/Img/banco/logo-venezuela.png') : require('@/assets/Img/banco/logo-mercatil.png')"
                    alt="Logo banco"
                    class="banco-logo-lg-img"
                  />
                  <div>
                    <h4 class="mb-0 font-weight-bold">{{ bancoSeleccionado === 'venezuela' ? 'Banco de Venezuela' : 'Mercantil' }}</h4>
                    <small class="text-muted">Datos para Pago Móvil</small>
                  </div>
                </div>
                <div class="dato-row"><span class="dato-label">Código:</span><span class="dato-valor font-mono">{{ bancoSeleccionado === 'venezuela' ? '0102' : '0105' }}</span></div>
                <div class="dato-row"><span class="dato-label">Cédula:</span><span class="dato-valor font-mono">V-18.731.281</span></div>
                <div class="dato-row"><span class="dato-label">Teléfono:</span><span class="dato-valor font-mono">04167594723</span></div>
                <div class="dato-row monto-row">
                  <span class="dato-label">Monto a Pagar:</span>
                  <div class="text-right">
                    <div class="dato-valor font-mono text-success">Bs. {{ montoEnBolivares.toLocaleString('es-VE', { minimumFractionDigits: 2 }) }}</div>
                    <small class="text-muted">Tasa: Bs. {{ tasaVirtual.toFixed(2) }} / USD</small>
                  </div>
                </div>
                <div class="d-flex gap-2 mt-3 flex-column flex-sm-row">
                  <button @click="copiarDatos" class="btn btn-copiar flex-grow-1"><i class="fas fa-copy mr-1"></i> Copiar</button>
                  <button @click="abrirModalQR" class="btn btn-qr flex-grow-1"><i class="fas fa-qrcode mr-1"></i> QR</button>
                </div>
                <button @click="abrirModalDatosPago" class="btn btn-ya-pague btn-block mt-3 py-3 font-weight-bold">
                  <i class="fas fa-cash-register mr-2"></i> Ya Pagué - Registrar en el Sistema
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- MODAL QR -->
    <transition name="fade-slide">
      <div v-if="showModalQR" class="modal-qr-overlay" @click.self="cerrarModalQR">
        <div class="modal-qr-card">
          <div class="modal-qr-header">
            <h3 class="modal-qr-title"><i class="fas fa-qrcode mr-2"></i> Escanear Código QR</h3>
            <button @click="cerrarModalQR" class="modal-qr-close"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-qr-body">
            <img
              :src="bancoSeleccionado === 'venezuela' ? require('@/assets/Img/banco/qr-venezuela.jpeg') : require('@/assets/Img/banco/qr-mercantil.png')"
              alt="Código QR"
              class="qr-img-modal"
            />
            <p class="text-center text-muted small mt-3 mb-0">
              <i class="fas fa-mobile-alt mr-1"></i> Escanee con su aplicación bancaria
            </p>
          </div>
        </div>
      </div>
    </transition>

    <!-- MODAL DATOS PAGO -->
    <transition name="fade-slide">
      <div v-if="showModalDatosPago" class="modal-datos-pago-overlay" @click.self="cerrarModalDatosPago">
        <div class="modal-datos-pago-card">
          <div class="modal-datos-pago-header">
            <h3 class="modal-datos-pago-title"><i class="fas fa-cash-register mr-2"></i> Registrar Pago en Sistema</h3>
            <button @click="cerrarModalDatosPago" class="modal-datos-pago-close"><i class="fas fa-times"></i></button>
          </div>
          <div class="modal-datos-pago-body">
            <div class="alert-info-pago mb-3">
              <i class="fas fa-info-circle mr-1"></i>
              Ingrese los datos de su transferencia o pago móvil. Su pago quedará registrado en el sistema <strong>en proceso de verificación</strong> hasta que el personal administrativo verifique los fondos en la cuenta bancaria.
            </div>
            <div class="form-group-pago mb-3">
              <label class="label-form-pago"><i class="fas fa-hashtag mr-1"></i> Número de Referencia Bancaria <span class="text-danger">*</span></label>
              <input type="text" v-model.trim="referenciaPago" class="input-pago" placeholder="Ej: 12345678" />
            </div>
            <div class="form-group-pago mb-3">
              <label class="label-form-pago"><i class="fas fa-university mr-1"></i> Banco Emisor (Desde donde pagó) <span class="text-danger">*</span></label>
              <select v-model="bancoEmisor" class="input-pago input-pago-select">
                <option value="" disabled>Seleccione su banco</option>
                <option v-for="banco in bancosVenezuela" :key="banco.codigo" :value="banco.nombre">{{ banco.codigo }} - {{ banco.nombre }}</option>
              </select>
            </div>
            <div class="form-group-pago mb-3">
              <label class="label-form-pago"><i class="fas fa-check-circle mr-1"></i> Cuenta Destino Jean Informático</label>
              <input type="text" :value="bancoSeleccionado === 'venezuela' ? 'Banco de Venezuela (0102) - CI 18731281' : 'Mercantil (0105) - CI 18731281'" class="input-pago" readonly />
            </div>
            <div class="form-group-pago mb-3">
              <label class="label-form-pago"><i class="fas fa-dollar-sign mr-1"></i> Monto a Registrar</label>
              <input type="text" :value="`Bs. ${montoEnBolivares.toLocaleString('es-VE', { minimumFractionDigits: 2 })} (${totalGeneralUsd.toFixed(2)} USD)`" class="input-pago" readonly />
            </div>
            <div class="alert-proceso-info mb-3">
              <i class="fas fa-shield-alt mr-1"></i>
              <div><strong>Verificación administrativa:</strong> El pago ingresa en estado "Pendiente" y el personal administrativo lo verificará directamente en la cuenta bancaria.</div>
            </div>
            <button
              @click="registrarPagoEnSistema"
              class="btn btn-registrar-sistema btn-lg btn-block py-3 font-weight-bold mt-3"
              :disabled="!referenciaPago || !bancoEmisor || registrandoPago"
            >
              <i :class="registrandoPago ? 'fas fa-spinner fa-spin mr-2' : 'fas fa-save mr-2'"></i>
              {{ registrandoPago ? 'Registrando en el sistema...' : 'Confirmar y Registrar Pago' }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { buscarOrdenPorNumero, validarCedulaYObtenerOrden } from '@/api/ordenes.js';
import { obtenerProductos } from '@/api/productos.js';
import { obtenerCategorias } from '@/api/categorias.js';
import { obtenerTasaVentaActual } from '@/api/tasaVenta.js';
import { crearPagoPendiente, obtenerPagosPorRecepcion } from '@/api/pagos.js';

import {
  ESTADOS,
  construirMenuPrincipal,
  construirMenuServicios,
  construirMenuPagosSinOrden,
  construirMenuPagosConOrden,
  construirUbicacion,
  construirAsesorHumano,
  procesarTextoLibre,
  formatearOrdenParaChat,
  formatearCategorias,
  formatearListaProductos,
  normalizar,
  ordenPermitePago
} from '@/services/chatbotFlujoService.js';

const HORA_MSG = () => new Date().toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });

export default {
  name: 'AsistenteOrdenModal',
  props: {
    autoOpen: { type: Boolean, default: false }
  },
  data() {
    return {
      abierto: false,
      mostrarPulso: false,
      mostrarBienvenida: true,
      botAvatarImg: require('@/assets/jean-bot.png'),
      hayResultado: false,
      cargando: false,
      botEscribiendo: false,
      orden: null,
      tasaVirtual: 1,
      mensajes: [],
      inputMensaje: '',
      catalogoProductos: [],
      categorias: [],
      cargandoCatalogo: false,

      // ── Flujo del chatbot ──────────────────────────────
      estadoChat: ESTADOS.MENU_PRINCIPAL,
      opcionesActuales: [],
      filtroCategoriaActual: null,
      ordenTemporal: null,
      contextoOrdenFlujo: null, // 'consulta' | 'pago'

      // ── Modales de pago ────────────────────────────────
      showModalPoliticas: false,
      showModalPago: false,
      showModalQR: false,
      showModalDatosPago: false,
      bancoSeleccionado: null,
      referenciaPago: '',
      bancoEmisor: '',
      registrandoPago: false,
      pagoRegistradoExitoso: false,
      pagosOrden: [],

      bancosVenezuela: [
        { codigo: '0102', nombre: 'Banco de Venezuela' },
        { codigo: '0104', nombre: 'Venezolano de Credito' },
        { codigo: '0105', nombre: 'Banco Mercantil' },
        { codigo: '0108', nombre: 'BBVA Provincial' },
        { codigo: '0114', nombre: 'Bancaribe' },
        { codigo: '0115', nombre: 'Banco Exterior' },
        { codigo: '0128', nombre: 'Banco Caroni' },
        { codigo: '0134', nombre: 'Banesco' },
        { codigo: '0137', nombre: 'Banco Sofitasa' },
        { codigo: '0138', nombre: 'Banco Plaza' },
        { codigo: '0146', nombre: 'Bangente' },
        { codigo: '0151', nombre: 'BFC Banco Fondo Comun' },
        { codigo: '0156', nombre: '100% Banco' },
        { codigo: '0157', nombre: 'Del Sur Banco Universal' },
        { codigo: '0163', nombre: 'Banco del Tesoro' },
        { codigo: '0166', nombre: 'Banco Agricola de Venezuela' },
        { codigo: '0168', nombre: 'Bancrecer' },
        { codigo: '0169', nombre: 'Mi Banco' },
        { codigo: '0171', nombre: 'Banco Activo' },
        { codigo: '0172', nombre: 'Bancamiga' },
        { codigo: '0174', nombre: 'Banplus' },
        { codigo: '0175', nombre: 'Banco Bicentenario' },
        { codigo: '0177', nombre: 'Banco de la Fuerza Armada (BANFANB)' },
        { codigo: '0190', nombre: 'Citibank' },
        { codigo: '0191', nombre: 'Banco Nacional de Credito (BNC)' }
      ],

      horaActual: new Date(),
      intervalHora: null,
      timeoutAutoOpen: null
    };
  },
  computed: {
    puedePagar() {
      const m = this.horaActual.getHours() * 60 + this.horaActual.getMinutes();
      return m >= 8 * 60 + 30 && m <= 17 * 60 + 30;
    },
    montoEnBolivares() {
      return (this.totalGeneralUsd || 0) * this.tasaVirtual;
    },
    puedeMostrarPago() {
      return !!this.orden && ordenPermitePago(this.orden);
    },
    totalGeneralUsd() {
      if (!this.orden) return 0;
      if (this.orden.precioTotalUsd !== undefined && this.orden.precioTotalUsd !== null && parseFloat(this.orden.precioTotalUsd) > 0) {
        return parseFloat(this.orden.precioTotalUsd) || 0;
      }
      const sum = (arr) => Array.isArray(arr) ? arr.reduce((s, i) => s + (parseFloat(i.subtotalUsd || i.precio || 0) || 0), 0) : 0;
      const det = sum(this.orden.recepcionEquipoDetalles || this.orden.detalles || []);
      if (det > 0) return det;
      return parseFloat(this.orden.montoServiciosUsd || 0) + parseFloat(this.orden.montoProductosUsd || 0) + parseFloat(this.orden.montoRepuestosUsd || 0);
    },
    placeholderInput() {
      switch (this.estadoChat) {
        case ESTADOS.ORDEN_PEDIR_NUMERO:
        case ESTADOS.PAGOS_PEDIR_ORDEN:
          return 'Ej: ORD-2024-001 o 105...';
        case ESTADOS.ORDEN_PEDIR_CEDULA:
          return 'Ej: 18731281...';
        case ESTADOS.PRECIOS_MENU_CATEGORIAS:
        case ESTADOS.PRECIOS_LISTA_PRODUCTOS:
          return 'Buscar producto por nombre...';
        default:
          return 'Escribe "menú" o elige una opción...';
      }
    }
  },
  mounted() {
    this.cargarCatalogo();
    this.intervalHora = setInterval(() => { this.horaActual = new Date(); }, 60000);
    this.mostrarBienvenida = true;
    try {
      sessionStorage.removeItem('jeanbot_welcome_dismissed');
    } catch (e) { /* ignore */ }
    if (this.autoOpen) {
      this.timeoutAutoOpen = setTimeout(() => { this.abrirAsistente(); }, 700);
    } else {
      this.mostrarPulso = true;
    }
  },
  beforeDestroy() {
    if (this.intervalHora) clearInterval(this.intervalHora);
    if (this.timeoutAutoOpen) clearTimeout(this.timeoutAutoOpen);
  },
  methods: {
    // ═══════════════════════════════════════════════════════
    // CATÁLOGO Y TASA
    // ═══════════════════════════════════════════════════════
    async cargarCatalogo() {
      this.cargandoCatalogo = true;
      try {
        const [prodRes, catsRes, tasaRes] = await Promise.all([
          obtenerProductos('activos').catch(() => ({ success: false, data: [] })),
          obtenerCategorias('activas').catch(() => ({ success: false, data: [] })),
          obtenerTasaVentaActual().catch(() => ({ success: false, data: null }))
        ]);

        const catMap = {};
        if (catsRes && catsRes.success && Array.isArray(catsRes.data)) {
          this.categorias = catsRes.data;
          catsRes.data.forEach((c) => {
            if (c && c.idCategoria && c.nombre) {
              catMap[c.idCategoria] = c.nombre.trim();
            }
          });
        }

        if (prodRes && prodRes.success && Array.isArray(prodRes.data)) {
          this.catalogoProductos = prodRes.data.map((p) => {
            const catNombre = (
              p.categoria ||
              p.nombreCategoria ||
              p.categoria_nombre ||
              p.nombre_categoria ||
              (p.idCategoria && catMap[p.idCategoria]) ||
              'Otros'
            ).trim();

            return {
              ...p,
              categoria: catNombre,
              nombreCategoria: catNombre,
              nombre: p.nombre || p.nombreProducto || 'Producto'
            };
          });
        }
        if (tasaRes && tasaRes.success && tasaRes.data) {
          const raw = tasaRes.data;
          const d = Array.isArray(raw) ? raw[0] : (raw.data || raw);
          const val = parseFloat(d?.precio_usd_bs || d?.tasa || d?.valor || d?.tasaVenta || d?.precioVenta || 0);
          if (val > 0) this.tasaVirtual = val;
        }
      } catch (err) {
        console.warn('Error cargando catálogo en asistente:', err);
      } finally {
        this.cargandoCatalogo = false;
      }
    },

    // ═══════════════════════════════════════════════════════
    // CHAT BÁSICO
    // ═══════════════════════════════════════════════════════
    pushMensaje(tipo, texto) {
      this.mensajes.push({ tipo, texto, hora: HORA_MSG() });
      this.$nextTick(this.scrollChat);
    },
    pushMensajeConOpciones(tipo, texto, opciones, productos) {
      this.mensajes.push({
        tipo,
        texto,
        hora: HORA_MSG(),
        opciones: opciones || [],
        productos: productos || null
      });
      this.$nextTick(this.scrollChat);
    },
    scrollChat() {
      const el = this.$refs.chatBody;
      if (el) el.scrollTop = el.scrollHeight;
    },
    botDice(texto, delay = 500) {
      this.botEscribiendo = true;
      return new Promise((resolve) => {
        setTimeout(() => {
          this.botEscribiendo = false;
          this.pushMensaje('bot', texto);
          resolve();
        }, delay);
      });
    },

    // ═══════════════════════════════════════════════════════
    // ABRIR / CERRAR / REINICIAR
    // ═══════════════════════════════════════════════════════
    cerrarBienvenida() {
      this.mostrarBienvenida = false;
    },
    onBotImgError(e) {
      if (e && e.target) {
        e.target.src = '/jean-bot.png';
      }
    },
    async abrirAsistente() {
      this.abierto = true;
      this.mostrarPulso = false;
      this.mostrarBienvenida = false;
      this.cargarCatalogo();
      this.$nextTick(() => {
        this.scrollChat();
        this.$refs.inputMensajeIa?.focus();
      });

      if (this.mensajes.length === 0) {
        this.botEscribiendo = true;
        
        // Simulamos el tiempo de "escribiendo..." y luego insertamos el mensaje con opciones
        setTimeout(() => {
          this.botEscribiendo = false;
          
          const menu = construirMenuPrincipal();
          
          // Insertamos el mensaje CON las opciones desde el principio
          this.mensajes.push({
            tipo: 'bot',
            texto: '¡Hola! 👋 Soy <strong>JeanBot</strong>, asistente virtual de <strong>Jean Informático C.A.</strong><br><br><strong>¿En qué te puedo ayudar?</strong>',
            hora: HORA_MSG(),
            opciones: menu.opciones || [], // ✅ Los chips van aquí
            productos: null
          });
          
          this.opcionesActuales = menu.opciones || [];
          this.estadoChat = ESTADOS.MENU_PRINCIPAL;
          
          this.$nextTick(this.scrollChat);
        }, 400);
      }
    },
    cerrarAsistente() {
      this.abierto = false;
      this.mostrarPulso = false;
    },
    reiniciarTodo() {
      this.orden = null;
      this.ordenTemporal = null;
      this.hayResultado = false;
      this.mensajes = [];
      this.estadoChat = ESTADOS.MENU_PRINCIPAL;
      this.opcionesActuales = [];
      this.contextoOrdenFlujo = null;
      this.abrirAsistente();
    },

    // ═══════════════════════════════════════════════════════
    // NAVEGACIÓN DE ESTADOS
    // ═══════════════════════════════════════════════════════
    irAEstado(estado) {
      this.estadoChat = estado;
      let menu;

      switch (estado) {
        case ESTADOS.MENU_PRINCIPAL:
          menu = construirMenuPrincipal();
          break;
        case ESTADOS.SERVICIOS_MENU:
          menu = construirMenuServicios();
          break;

        // ⭐ REGLA CLAVE: pagos condicional según orden cargada
        case ESTADOS.PAGOS_MENU:
          menu = this.orden
            ? construirMenuPagosConOrden(this.orden)
            : construirMenuPagosSinOrden();
          break;

        case ESTADOS.PAGOS_PEDIR_ORDEN:
          menu = {
            texto:
              '📝 Escríbeme el <strong>número de orden</strong> a la que deseas asociar el pago.<br>' +
              'Ejemplo: <em>ORD-2024-001</em> o <em>105</em>.',
            opciones: [{ id: 'cancel', label: '⬅️ Cancelar', accion: ESTADOS.PAGOS_MENU }]
          };
          break;

        case ESTADOS.UBICACION:
          menu = construirUbicacion();
          break;
        case ESTADOS.ASESOR_HUMANO:
          menu = construirAsesorHumano();
          break;
        case ESTADOS.PRECIOS_MENU_CATEGORIAS:
          menu = formatearCategorias(this.catalogoProductos, this.categorias);
          break;
        case ESTADOS.ORDEN_PEDIR_NUMERO:
          menu = {
            texto:
              '🔍 Por favor escríbeme tu <strong>número de orden</strong>.<br>' +
              'Ejemplo: <em>ORD-2024-001</em> o <em>105</em>.',
            opciones: [{ id: 'cancel', label: '⬅️ Cancelar', accion: ESTADOS.MENU_PRINCIPAL }]
          };
          break;
        default:
          menu = construirMenuPrincipal();
      }

      this.opcionesActuales = menu.opciones || [];
      this.pushMensajeConOpciones('bot', menu.texto, menu.opciones, menu.productos);
    },

    // ═══════════════════════════════════════════════════════
    // EJECUTAR OPCIÓN CLICKEADA
    // ═══════════════════════════════════════════════════════
    async ejecutarOpcion(opcion) {
      if (this.botEscribiendo) return;
      this.pushMensaje('user', opcion.label);
      this.botEscribiendo = true;
      await new Promise((r) => setTimeout(r, 250));
      this.botEscribiendo = false;

      // URLs externas
      if (opcion.url) {
        window.open(opcion.url, '_blank');
        return;
      }

      // WhatsApp con contexto de orden
      if (opcion.accion === 'ABRIR_WHATSAPP_ORDEN') {
        const n = this.orden?.numeroOrden || this.orden?.idRecepcionEquipo || 'S/N';
        window.open(
          `https://wa.me/584125696219?text=${encodeURIComponent('Hola, consulta sobre orden #' + n)}`,
          '_blank'
        );
        return;
      }

      // ⭐ ABRIR MODAL DE PAGO (con re-validación de seguridad)
      if (opcion.accion === 'ABRIR_MODAL_PAGO') {
        if (!this.orden || !ordenPermitePago(this.orden)) {
          this.pushMensaje(
            'bot',
            '⏳ Tu equipo aún no está reparado o listo para retirar. El pago se habilita cuando el estado sea <strong>reparado</strong> o <strong>listo</strong>.'
          );
          return;
        }
        this.abrirModalPago();
        return;
      }

      // Ver estado de la orden ya cargada
      if (opcion.accion === 'VER_ESTADO_ORDEN_ACTUAL') {
        if (this.orden) {
          const total = this.totalGeneralUsd;
          const totalBs = total * this.tasaVirtual;
          const fmt = formatearOrdenParaChat(this.orden, total, totalBs);
          this.opcionesActuales = fmt.opciones;
          this.pushMensajeConOpciones('bot', fmt.texto, fmt.opciones);
        } else {
          this.irAEstado(ESTADOS.ORDEN_PEDIR_NUMERO);
        }
        return;
      }

      // Filtros de categoría
      if (opcion.accion === 'FILTRAR_CATEGORIA') {
        this.filtroCategoriaActual = opcion.categoria;
        const normTarget = opcion.categoria ? normalizar(opcion.categoria) : null;
        const filtrados = opcion.categoria
          ? this.catalogoProductos.filter((p) => {
              if (opcion.idCategoria && (p.idCategoria == opcion.idCategoria || p.id_categoria == opcion.idCategoria)) {
                return true;
              }
              const pCat = normalizar(p.categoria);
              const pNomCat = normalizar(p.nombreCategoria);
              const pCatNom = normalizar(p.categoria_nombre);
              return (
                pCat === normTarget ||
                pNomCat === normTarget ||
                pCatNom === normTarget ||
                (pCat && normTarget && (pCat.includes(normTarget) || normTarget.includes(pCat))) ||
                (pNomCat && normTarget && (pNomCat.includes(normTarget) || normTarget.includes(pNomCat)))
              );
            })
          : this.catalogoProductos;
        const lista = formatearListaProductos(filtrados, opcion.categoria, this.tasaVirtual);
        this.estadoChat = ESTADOS.PRECIOS_LISTA_PRODUCTOS;
        this.opcionesActuales = lista.opciones;
        this.pushMensajeConOpciones('bot', lista.texto, lista.opciones, lista.productos);
        return;
      }

      // Servicios con detalle
      if (opcion.accion && opcion.accion.startsWith('SERVICIO_')) {
        this.pushDetalleServicio(opcion.accion);
        return;
      }

      // Navegación entre estados
      if (ESTADOS[opcion.accion]) {
        this.irAEstado(opcion.accion);
      }
    },

    pushDetalleServicio(codigo) {
      const detalles = {
        SERVICIO_COMPUTACION: {
          texto:
            '💻 <strong>Computadoras y Laptops</strong><br><br>' +
            '• Mantenimiento preventivo (limpieza + pasta térmica)<br>' +
            '• Formateo Windows / Mac + drivers<br>' +
            '• Instalación SSD / RAM<br>' +
            '• Reparación de tarjeta madre<br>' +
            '• Recuperación de datos',
          opciones: [
            { id: 'srv_cotiza', label: '💬 Cotizar este servicio', accion: 'ABRIR_WHATSAPP' },
            { id: 'srv_volver', label: '⬅️ Volver a servicios', accion: ESTADOS.SERVICIOS_MENU }
          ]
        },
        SERVICIO_TELEFONOS: {
          texto:
            '📱 <strong>Teléfonos Inteligentes</strong><br><br>' +
            '• Cambio de pantalla (original / genérica)<br>' +
            '• Reemplazo de batería<br>' +
            '• Puerto de carga<br>' +
            '• Liberación / flasheo de software<br>' +
            '• Diagnóstico gratuito',
          opciones: [
            { id: 'srv_cotiza', label: '💬 Cotizar este servicio', accion: 'ABRIR_WHATSAPP' },
            { id: 'srv_volver', label: '⬅️ Volver a servicios', accion: ESTADOS.SERVICIOS_MENU }
          ]
        },
        SERVICIO_REDES: {
          texto:
            '🌐 <strong>Redes e Internet</strong><br><br>' +
            '• Configuración de routers Wi-Fi<br>' +
            '• Puntos de acceso y switches<br>' +
            '• Cableado estructurado<br>' +
            '• Antenas de largo alcance',
          opciones: [
            { id: 'srv_cotiza', label: '💬 Cotizar este servicio', accion: 'ABRIR_WHATSAPP' },
            { id: 'srv_volver', label: '⬅️ Volver a servicios', accion: ESTADOS.SERVICIOS_MENU }
          ]
        },
        SERVICIO_SEGURIDAD: {
          texto:
            '📷 <strong>Cámaras y Alarmas</strong><br><br>' +
            '• Instalación de cámaras CCTV<br>' +
            '• Cámaras IP / Wi-Fi con app móvil<br>' +
            '• DVR y monitoreo remoto<br>' +
            '• Sistemas de alarma',
          opciones: [
            { id: 'srv_cotiza', label: '💬 Cotizar este servicio', accion: 'ABRIR_WHATSAPP' },
            { id: 'srv_volver', label: '⬅️ Volver a servicios', accion: ESTADOS.SERVICIOS_MENU }
          ]
        }
      };

      if (codigo === 'ABRIR_WHATSAPP') {
        window.open('https://wa.me/584125696219?text=Hola,%20quiero%20cotizar%20un%20servicio', '_blank');
        return;
      }

      const d = detalles[codigo];
      if (d) {
        this.opcionesActuales = d.opciones;
        this.pushMensajeConOpciones('bot', d.texto, d.opciones);
      }
    },

    // ═══════════════════════════════════════════════════════
    // ENVÍO DE TEXTO LIBRE
    // ═══════════════════════════════════════════════════════
    async enviarMensajeIa() {
      const msg = (this.inputMensaje || '').trim();
      if (!msg || this.botEscribiendo) return;
      this.inputMensaje = '';
      this.pushMensaje('user', msg);
      this.botEscribiendo = true;

      await new Promise((r) => setTimeout(r, 200));

      try {
        const resultado = procesarTextoLibre(msg, this.estadoChat);
        this.botEscribiendo = false;

        if (resultado.accion === 'BUSCAR_ORDEN') {
          await this.buscarOrdenFlujo(resultado.numeroOrden, resultado.contexto || 'consulta');
        } else if (resultado.accion === 'VALIDAR_CEDULA') {
          await this.validarCedulaFlujo(resultado.cedula);
        } else if (resultado.accion === 'BUSCAR_PRODUCTO') {
          this.filtrarProductosPorTexto(resultado.termino);
        } else if (resultado.accion && ESTADOS[resultado.accion]) {
          this.irAEstado(resultado.accion);
        } else {
          this.pushMensaje('bot', resultado.texto || 'No entendí tu mensaje.');
        }
      } catch (err) {
        console.error(err);
        this.botEscribiendo = false;
        this.pushMensaje('bot', '⚠️ Ocurrió un error. Escribe "menú" para reiniciar.');
      }
    },

    // ═══════════════════════════════════════════════════════
    // BÚSQUEDA DE ORDEN
    // ═══════════════════════════════════════════════════════
    async buscarOrdenFlujo(numeroOrden, contexto = 'consulta') {
      this.botEscribiendo = true;
      try {
        const res = await buscarOrdenPorNumero(numeroOrden);
        this.botEscribiendo = false;

        if (res.success && res.data) {
          this.ordenTemporal = res.data;
          this.estadoChat = ESTADOS.ORDEN_PEDIR_CEDULA;
          this.opcionesActuales = [];
          this.contextoOrdenFlujo = contexto;
          this.pushMensaje(
            'bot',
            `¡Encontré la orden <strong>#${res.data.numeroOrden || res.data.idRecepcionEquipo}</strong>! 🔍<br><br>` +
              `🔒 Por seguridad, escríbeme la <strong>cédula del titular</strong> (ej: <em>18731281</em>).`
          );
        } else {
          this.irAEstado(
            contexto === 'pago' ? ESTADOS.PAGOS_PEDIR_ORDEN : ESTADOS.ORDEN_PEDIR_NUMERO
          );
          this.pushMensaje('bot', `❌ No encontré la orden "${numeroOrden}". Intenta de nuevo.`);
        }
      } catch {
        this.botEscribiendo = false;
        this.pushMensaje('bot', '⚠️ Error de conexión. Intenta más tarde.');
      }
    },

    async validarCedulaFlujo(cedula) {
      this.botEscribiendo = true;
      try {
        const res = await validarCedulaYObtenerOrden(this.ordenTemporal, cedula);
        this.botEscribiendo = false;

        if (res.success && res.data) {
          this.orden = res.data;
          this.hayResultado = true;
          this.estadoChat = ESTADOS.ORDEN_RESULTADO;

          const total = this.totalGeneralUsd;
          const totalBs = total * this.tasaVirtual;
          const fmt = formatearOrdenParaChat(res.data, total, totalBs);

          this.opcionesActuales = fmt.opciones;
          this.pushMensajeConOpciones('bot', fmt.texto, fmt.opciones);

          if (this.contextoOrdenFlujo === 'pago' && ordenPermitePago(res.data)) {
            this.pushMensaje(
              'bot',
              '💳 Como tu equipo ya está listo, puedes registrar tu pago ahora con el botón <strong>"Ir a pagar"</strong>.'
            );
          }
          this.contextoOrdenFlujo = null;
        } else {
          this.pushMensaje(
            'bot',
            '❌ La cédula no coincide con el titular de la orden.<br>' +
              'Inténtalo de nuevo o escribe <em>"menú"</em> para reiniciar.'
          );
        }
      } catch {
        this.botEscribiendo = false;
        this.pushMensaje('bot', '⚠️ Error al validar. Intenta de nuevo.');
      }
    },

    filtrarProductosPorTexto(termino) {
      const t = normalizar(termino);
      const filtrados = this.catalogoProductos.filter(
        (p) =>
          normalizar(p.nombre).includes(t) ||
          normalizar(p.nombreProducto).includes(t) ||
          normalizar(p.categoria).includes(t) ||
          normalizar(p.nombreCategoria).includes(t) ||
          normalizar(p.codigo).includes(t)
      );
      const lista = formatearListaProductos(filtrados, `Búsqueda: "${termino}"`, this.tasaVirtual);
      this.opcionesActuales = lista.opciones;
      this.pushMensajeConOpciones('bot', lista.texto, lista.opciones, lista.productos);
    },

    // ═══════════════════════════════════════════════════════
    // PAGOS
    // ═══════════════════════════════════════════════════════
    abrirModalPago() {
      if (!this.puedePagar) {
        this.$alert('Los pagos solo están disponibles de 8:30 AM a 5:30 PM.', 'Fuera de Horario', 'warning');
        return;
      }
      this.showModalPoliticas = true;
    },
    cerrarModalPoliticas() {
      this.showModalPoliticas = false;
      if (this.puedePagar) {
        this.showModalPago = true;
        this.bancoSeleccionado = null;
      }
    },
    cerrarModalPago() {
      this.showModalPago = false;
      this.bancoSeleccionado = null;
    },
    seleccionarBanco(b) { this.bancoSeleccionado = b; },
    copiarDatos() {
      const codigo = this.bancoSeleccionado === 'venezuela' ? '0102' : '0105';
      const texto = `${codigo}\n18731281\n04125696219\n${this.montoEnBolivares.toFixed(2)}`;
      if (navigator.clipboard) {
        navigator.clipboard
          .writeText(texto)
          .then(() => this.$alert('Datos copiados', 'Listo!', 'success'))
          .catch(() => this.fallbackCopiar(texto));
      } else this.fallbackCopiar(texto);
    },
    fallbackCopiar(t) {
      const ta = document.createElement('textarea');
      ta.value = t;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      this.$alert('Datos copiados', 'Listo!', 'success');
    },
    abrirModalQR() { this.showModalQR = true; },
    cerrarModalQR() { this.showModalQR = false; },
    abrirModalDatosPago() {
      this.showModalDatosPago = true;
      this.referenciaPago = '';
      this.bancoEmisor = '';
    },
    cerrarModalDatosPago() { this.showModalDatosPago = false; },

    async registrarPagoEnSistema() {
      if (!this.referenciaPago.trim() || !this.bancoEmisor.trim()) {
        this.$alert('Por favor complete el número de referencia y el banco emisor.', 'Campos requeridos', 'warning');
        return;
      }
      if (!this.orden || !this.orden.idRecepcionEquipo) {
        this.$alert('No hay una orden de servicio activa seleccionada.', 'Atención', 'warning');
        return;
      }
      // ⭐ Doble verificación de seguridad
      if (!ordenPermitePago(this.orden)) {
        this.$alert(
          'El equipo aún no está reparado o listo para retirar. No es posible registrar el pago.',
          'Pago no habilitado',
          'warning'
        );
        return;
      }

      this.registrandoPago = true;
      try {
        const nOrden = this.orden?.numeroOrden || this.orden?.idRecepcionEquipo || 'S/N';
        const bancoRec =
          this.bancoSeleccionado === 'venezuela' ? 'Banco de Venezuela (0102)' : 'Mercantil (0105)';
        const mBs = this.montoEnBolivares;
        const mUsd = this.totalGeneralUsd;
        const refLimpia = this.referenciaPago.trim();

        const res = await crearPagoPendiente({
          idRecepcionEquipo: this.orden.idRecepcionEquipo,
          monto: mBs > 0 ? mBs : mUsd,
          moneda: 'BS',
          referencia: refLimpia,
          descripcion: `[PAGO WEB] Orden #${nOrden} - Desde ${this.bancoEmisor} hacia ${bancoRec} - Titular: ${this.orden?.nombreCliente || ''}`,
          idMetodoPago: 3,
          idUsuario: 1
        });

        if (res.success) {
          this.pagoRegistradoExitoso = true;
          this.cerrarModalDatosPago();
          this.cerrarModalPago();

          await this.botDice(
            `✅ <strong>¡Pago Registrado Exitosamente en el Sistema!</strong><br><br>` +
              `📋 <strong>Orden:</strong> #${nOrden}<br>` +
              `🔢 <strong>N° Referencia:</strong> <code style="background:#e2e8f0;padding:2px 6px;border-radius:4px;color:#0f172a;font-weight:700;">${refLimpia}</code><br>` +
              `🏦 <strong>Banco:</strong> ${this.bancoEmisor} &rarr; ${bancoRec}<br>` +
              `💵 <strong>Monto Reportado:</strong> Bs. ${mBs.toLocaleString('es-VE', { minimumFractionDigits: 2 })} (${mUsd.toFixed(2)} USD)<br><br>` +
              `⏳ <strong>Estado: En Proceso de Verificación Bancaria</strong><br>` +
              `<em>Tu pago ha sido registrado en nuestro sistema administrativo. El personal de caja verificará el abono en la cuenta bancaria para validar la orden.</em>`
          );

          this.$alert(
            `Su pago con referencia ${refLimpia} ha sido registrado en el sistema. Queda en proceso de verificación por el personal administrativo.`,
            '¡Pago Registrado!',
            'success'
          );

          const pRes = await obtenerPagosPorRecepcion(this.orden.idRecepcionEquipo);
          if (pRes.success) this.pagosOrden = pRes.data;
        } else {
          throw new Error(res.error || 'No se pudo registrar el pago en el servidor');
        }
      } catch (err) {
        console.error('Error al registrar pago en el sistema:', err);
        this.$alert(
          err.message || 'Error al registrar el pago. Verifique e intente nuevamente.',
          'Error',
          'error'
        );
      } finally {
        this.registrandoPago = false;
      }
    }
  }
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   ASISTENTE FLOTANTE JEAN-BOT (PERSONAJE COMPLETO SIN CÍRCULO)
═══════════════════════════════════════════════════════════ */
.jeanbot-floating-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 10000;
  display: flex;
  align-items: flex-end;
  gap: 14px;
  pointer-events: none;
}
.jeanbot-floating-container > * {
  pointer-events: auto;
}

/* ── TARJETA DE BIENVENIDA Y PUBLICIDAD (CON BOTÓN 'X') ───── */
.jeanbot-welcome-card {
  position: relative;
  background: #ffffff;
  border-radius: 18px;
  border: 1.5px solid rgba(56, 189, 248, 0.5);
  box-shadow:
    0 20px 45px -10px rgba(15, 23, 42, 0.32),
    0 8px 20px -4px rgba(2, 132, 199, 0.25);
  padding: 14px 16px 12px 16px;
  max-width: 320px;
  cursor: pointer;
  margin-bottom: 12px;
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  user-select: none;
  z-index: 10001;
}
.jeanbot-welcome-card:hover {
  transform: translateY(-3px) scale(1.02);
  border-color: #0284c7;
  box-shadow:
    0 24px 50px -10px rgba(15, 23, 42, 0.38),
    0 12px 24px -4px rgba(2, 132, 199, 0.35);
}

.welcome-card-close {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.78rem;
  transition: all 0.2s ease;
  z-index: 5;
}
.welcome-card-close:hover {
  background: #fee2e2;
  color: #ef4444;
  border-color: #fecaca;
  transform: scale(1.12) rotate(90deg);
}

.welcome-card-body {
  display: flex;
  flex-direction: column;
  text-align: left;
}
.welcome-card-header {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding-right: 24px; /* Espacio para no chocar con el botón X */
}
.welcome-wave-icon {
  font-size: 1.35rem;
  line-height: 1;
  display: inline-block;
  transform-origin: 70% 70%;
  animation: welcomeWave 2.4s infinite;
}
@keyframes welcomeWave {
  0% { transform: rotate(0deg); }
  10% { transform: rotate(14deg); }
  20% { transform: rotate(-8deg); }
  30% { transform: rotate(14deg); }
  40% { transform: rotate(-4deg); }
  50% { transform: rotate(10deg); }
  60%, 100% { transform: rotate(0deg); }
}
.welcome-header-texts {
  display: flex;
  flex-direction: column;
}
.welcome-title {
  font-size: 0.88rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0;
  line-height: 1.25;
}
.welcome-badge-tag {
  align-self: flex-start;
  margin-top: 3px;
  background: #e0f2fe;
  color: #0284c7;
  font-size: 0.65rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 10px;
  letter-spacing: 0.2px;
}
.welcome-message {
  font-size: 0.77rem;
  color: #475569;
  line-height: 1.42;
  margin: 8px 0 10px 0;
}
.welcome-message strong {
  color: #0284c7;
}
.welcome-card-footer {
  display: flex;
  align-items: center;
}
.welcome-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, #0284c7 0%, #0369a1 100%);
  color: #ffffff;
  font-size: 0.74rem;
  font-weight: 700;
  padding: 5px 12px;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(2, 132, 199, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.jeanbot-welcome-card:hover .welcome-cta-btn {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(2, 132, 199, 0.45);
}

.welcome-bubble-pointer {
  position: absolute;
  right: -9px;
  bottom: 26px;
  width: 0;
  height: 0;
  border-top: 9px solid transparent;
  border-bottom: 9px solid transparent;
  border-left: 9px solid #ffffff;
  filter: drop-shadow(2px 0 2px rgba(0, 0, 0, 0.08));
}

/* Transición para la tarjeta */
.welcome-card-anim-enter-active,
.welcome-card-anim-leave-active {
  transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1);
}
.welcome-card-anim-enter {
  opacity: 0;
  transform: translateY(12px) scale(0.92);
}
.welcome-card-anim-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.92);
}

/* ── PERSONAJE JEAN-BOT FLOTANTE (SIN CÍRCULO) ─────────────── */
.jeanbot-mascot-wrapper {
  position: relative;
  width: 125px;
  height: 142px;
  cursor: pointer;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  user-select: none;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.jeanbot-mascot-img {
  width: auto;
  height: 135px;
  max-width: 125px;
  object-fit: contain;
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.38)) drop-shadow(0 2px 8px rgba(2, 132, 199, 0.4));
  animation: mascotFloat 3.8s ease-in-out infinite;
  transform-origin: center bottom;
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), filter 0.35s ease;
  pointer-events: none;
}
.jeanbot-mascot-wrapper:hover .jeanbot-mascot-img {
  transform: scale(1.08) translateY(-4px) rotate(-1.5deg);
  filter: drop-shadow(0 16px 26px rgba(0, 0, 0, 0.48)) drop-shadow(0 0 16px rgba(56, 189, 248, 0.65));
  animation-play-state: paused;
}
.jeanbot-mascot-wrapper:active .jeanbot-mascot-img {
  transform: scale(0.96) translateY(2px);
}
@keyframes mascotFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-9px);
  }
}

/* Sombra dinámica en el suelo */
.mascot-ground-shadow {
  position: absolute;
  bottom: 2px;
  left: 50%;
  transform: translateX(-50%);
  width: 82px;
  height: 14px;
  border-radius: 50%;
  background: radial-gradient(ellipse at center, rgba(15, 23, 42, 0.45) 0%, rgba(15, 23, 42, 0.15) 50%, transparent 75%);
  animation: groundShadowPulse 3.8s ease-in-out infinite;
  pointer-events: none;
}
@keyframes groundShadowPulse {
  0%, 100% {
    transform: translateX(-50%) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: translateX(-50%) scale(0.82);
    opacity: 0.4;
  }
}

/* Resplandor ambiental */
.mascot-ambient-glow {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  width: 90px;
  height: 90px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.28) 0%, transparent 70%);
  filter: blur(12px);
  pointer-events: none;
  z-index: -1;
  animation: ambientGlowBreathe 4s ease-in-out infinite alternate;
}
@keyframes ambientGlowBreathe {
  0% { transform: translateX(-50%) scale(0.9); opacity: 0.5; }
  100% { transform: translateX(-50%) scale(1.2); opacity: 0.9; }
}

/* Pill flotante 'En línea' */
.mascot-online-pill {
  position: absolute;
  bottom: 12px;
  right: 2px;
  background: rgba(15, 23, 42, 0.88);
  backdrop-filter: blur(6px);
  border: 1px solid rgba(56, 189, 248, 0.45);
  padding: 2px 8px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.35);
  pointer-events: none;
  z-index: 2;
}
.mascot-online-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 6px #10b981;
}
.mascot-online-ping {
  position: absolute;
  left: 8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: rgba(16, 185, 129, 0.7);
  animation: mascotPing 1.8s cubic-bezier(0, 0, 0.2, 1) infinite;
}
@keyframes mascotPing {
  0% { transform: scale(1); opacity: 0.9; }
  75%, 100% { transform: scale(2.8); opacity: 0; }
}
.mascot-online-label {
  color: #f8fafc;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.2px;
}

/* Badge de notificación */
.mascot-status-badge {
  position: absolute;
  top: 8px;
  right: 6px;
  background: #10b981;
  color: white;
  border: 2px solid white;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  font-size: 0.72rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 8px rgba(16, 185, 129, 0.6);
  z-index: 3;
}

/* ═══════════════════════════════════════════════════════════
   OVERLAY + VENTANA
═══════════════════════════════════════════════════════════ */
.asistente-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(6px);
  z-index: 9999;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 1rem;
}
.asistente-window {
  width: 100%;
  max-width: 400px;
  height: calc(100vh - 80px);
  max-height: 680px;
  background: #f8fafc;
  border-radius: 1.25rem;
  box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin-right: 4px;
}

/* ═══════════════════════════════════════════════════════════
   HEADER
═══════════════════════════════════════════════════════════ */
.asistente-header {
  background: linear-gradient(135deg, #0d1b3e 0%, #1e3a8a 100%);
  color: white;
  padding: 0.85rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  position: relative;
  overflow: hidden;
}
.asistente-header::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -30%;
  width: 240px;
  height: 240px;
  background: radial-gradient(circle, rgba(56, 189, 248, 0.2) 0%, transparent 70%);
  pointer-events: none;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  position: relative;
  z-index: 1;
  min-width: 0;
}
.bot-avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0d1b3e 0%, #1e3a8a 100%);
  border: 2px solid rgba(56, 189, 248, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
  box-shadow: 0 0 14px rgba(56, 189, 248, 0.35);
  overflow: visible;
}
.bot-avatar-header-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: 50% 12%;
  transform: scale(1.18);
  animation: headerBotGlow 3.5s ease-in-out infinite alternate;
}
@keyframes headerBotGlow {
  0% { transform: scale(1.18); }
  100% { transform: scale(1.22) translateY(-1px); }
}
.online-dot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 11px;
  height: 11px;
  background: #10b981;
  border: 2px solid #0d1b3e;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.8);
}
.bot-name { font-size: 0.95rem; font-weight: 800; margin: 0; line-height: 1.2; }
.bot-status { font-size: 0.72rem; color: #94a3b8; margin: 0; display: flex; align-items: center; gap: 4px; }
.header-actions { display: flex; gap: 0.4rem; position: relative; z-index: 1; flex-shrink: 0; }
.header-btn {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  width: 34px;
  height: 34px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;
}
.header-btn:hover { background: rgba(255, 255, 255, 0.3); }
.header-btn-close:hover { background: rgba(239, 68, 68, 0.8); }

/* ═══════════════════════════════════════════════════════════
   BODY
═══════════════════════════════════════════════════════════ */
.asistente-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  scroll-behavior: smooth;
}
.asistente-body::-webkit-scrollbar { width: 6px; }
.asistente-body::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
.asistente-body::-webkit-scrollbar-thumb:hover { background: #94a3b8; }

/* ═══════════════════════════════════════════════════════════
   MENSAJES
═══════════════════════════════════════════════════════════ */
.chat-messages { display: flex; flex-direction: column; gap: 0.65rem; }
.msg-row { display: flex; align-items: flex-end; gap: 0.5rem; max-width: 100%; }
.msg-bot { justify-content: flex-start; }
.msg-user { justify-content: flex-end; }
.msg-avatar-bot {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #0d1b3e 0%, #1e3a8a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 0 0 2.5px rgba(56, 189, 248, 0.35), 0 3px 8px -2px rgba(13, 27, 62, 0.4);
  position: relative;
  overflow: hidden;
}
.msg-avatar-bot-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: 50% 12%;
  transform: scale(1.22);
}
.msg-avatar-bot-img.is-typing {
  animation: typingBotBob 0.8s ease-in-out infinite alternate;
}
@keyframes typingBotBob {
  0% { transform: scale(1.22) translateY(0); }
  100% { transform: scale(1.26) translateY(-2px); }
}
.msg-avatar-bot::after {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 50%;
  border: 1.5px solid rgba(56, 189, 248, 0.35);
  animation: avatarGlow 2.4s ease-in-out infinite;
  pointer-events: none;
}
@keyframes avatarGlow {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.08); }
}
.msg-bubble {
  padding: 0.6rem 0.85rem;
  border-radius: 1rem;
  font-size: 0.85rem;
  line-height: 1.4;
  max-width: 82%;
  word-wrap: break-word;
  animation: msgAppear 0.25s ease;
  position: relative;
}
@keyframes msgAppear {
  from { opacity: 0; transform: translateY(6px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.bubble-bot {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  color: #0f172a;
  border-bottom-left-radius: 0.3rem;
  box-shadow: 0 4px 14px -4px rgba(15, 23, 42, 0.12), 0 1px 2px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(226, 232, 240, 0.9);
  backdrop-filter: blur(4px);
}
.bubble-user {
  background: linear-gradient(135deg, #10b981 0%, #059669 55%, #047857 100%);
  color: white;
  border-bottom-right-radius: 0.3rem;
  box-shadow: 0 6px 16px -4px rgba(16, 185, 129, 0.55);
}
.msg-hora { font-size: 0.62rem; opacity: 0.7; margin-top: 0.25rem; text-align: right; }

/* ═══════════════════════════════════════════════════════════
   TYPING
═══════════════════════════════════════════════════════════ */
.typing-bubble {
  display: flex;
  gap: 5px;
  padding: 0.75rem 1rem;
  background: linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%);
}
.typing-dot {
  width: 8px;
  height: 8px;
  background: linear-gradient(135deg, #94a3b8, #64748b);
  border-radius: 50%;
  animation: typingBounce 1.3s ease-in-out infinite;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
.typing-dot:nth-child(2) { animation-delay: 0.15s; }
.typing-dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes typingBounce {
  0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
  30% { transform: translateY(-5px); opacity: 1; }
}

/* ═══════════════════════════════════════════════════════════
   INPUT
═══════════════════════════════════════════════════════════ */
.input-chat-group {
  display: flex;
  align-items: stretch;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 2px solid #e2e8f0;
  border-radius: 9999px;
  padding: 0.25rem 0.25rem 0.25rem 1rem;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  box-shadow: 0 6px 18px -6px rgba(15, 23, 42, 0.12);
}
.input-chat-group:focus-within {
  border-color: #10b981;
  box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.15), 0 8px 22px -8px rgba(16, 185, 129, 0.35);
  transform: translateY(-1px);
}
.input-icon { color: #94a3b8; font-size: 0.9rem; align-self: center; margin-right: 0.5rem; }
.chat-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 0.9rem;
  padding: 0.65rem 0.25rem;
  color: #0f172a;
}
.chat-input::placeholder { color: #94a3b8; font-weight: 500; }
.btn-send {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border: none;
  color: white;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;
  font-size: 0.95rem;
  box-shadow: 0 6px 14px -3px rgba(16, 185, 129, 0.5);
}
.btn-send:hover:not(:disabled) {
  transform: scale(1.1) rotate(-6deg);
  box-shadow: 0 10px 20px -4px rgba(16, 185, 129, 0.7);
}
.btn-send:disabled { opacity: 0.5; cursor: not-allowed; }

/* ═══════════════════════════════════════════════════════════
   QUICK REPLIES (CHIPS)
═══════════════════════════════════════════════════════════ */
.msg-opciones-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
}
.btn-opcion-chip {
  background: linear-gradient(180deg, #ffffff, #f1f5f9);
  border: 1.5px solid #10b981;
  color: #047857;
  border-radius: 1rem;
  padding: 0.4rem 0.8rem;
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px -2px rgba(16, 185, 129, 0.25);
  white-space: nowrap;
  text-align: left;
  line-height: 1.2;
}
.btn-opcion-chip:hover:not(:disabled) {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 6px 14px -3px rgba(16, 185, 129, 0.55);
}
.btn-opcion-chip:disabled { opacity: 0.5; cursor: not-allowed; }

/* ═══════════════════════════════════════════════════════════
   BADGE DE ESTADO
═══════════════════════════════════════════════════════════ */
.badge-estado-pill {
  padding: 0.35rem 0.8rem;
  border-radius: 9999px;
  font-weight: 800;
  font-size: 0.68rem;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  letter-spacing: 0.03em;
  box-shadow: 0 3px 8px -2px rgba(0, 0, 0, 0.15);
}

/* ═══════════════════════════════════════════════════════════
   INPUT FIJO AL FONDO
═══════════════════════════════════════════════════════════ */
.chat-input-area-sticky {
  background: white;
  border-top: 1px solid #e2e8f0;
  padding: 0.65rem 0.85rem;
  flex-shrink: 0;
}

/* ═══════════════════════════════════════════════════════════
   TARJETAS DE PRODUCTOS DENTRO DEL CHAT
═══════════════════════════════════════════════════════════ */
.msg-productos-grid {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  max-height: 420px;
  overflow-y: auto;
  padding-right: 4px;
}
.bot-inline-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.65rem;
  padding: 0.55rem;
  display: flex;
  gap: 0.55rem;
  align-items: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}
.inline-card-thumb {
  width: 50px;
  height: 50px;
  border-radius: 0.4rem;
  background: #f1f5f9;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.inline-card-thumb img { width: 100%; height: 100%; object-fit: cover; }
.inline-card-ph { color: #94a3b8; font-size: 1.2rem; }
.inline-card-content { flex: 1; min-width: 0; }
.inline-card-title { font-size: 0.8rem; font-weight: 700; color: #0f172a; line-height: 1.2; }
.inline-card-tag { font-size: 0.62rem; color: #64748b; }
.inline-card-prices { display: flex; gap: 0.35rem; align-items: baseline; margin: 0.2rem 0; }
.price-usd-strong { color: #059669; font-weight: 800; font-size: 0.88rem; }
.price-bs-small { color: #64748b; font-size: 0.7rem; }

/* ═══════════════════════════════════════════════════════════
   MODALES
═══════════════════════════════════════════════════════════ */
.modal-politicas-overlay,
.modal-pago-overlay,
.modal-qr-overlay,
.modal-datos-pago-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.85);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
}
.modal-politicas-overlay { z-index: 10001; }
.modal-pago-overlay { z-index: 10002; }
.modal-qr-overlay { z-index: 10003; }
.modal-datos-pago-overlay { z-index: 10004; }

.modal-politicas-card,
.modal-pago-card,
.modal-datos-pago-card {
  background: #fff;
  border-radius: 1.25rem;
  width: 100%;
  max-width: 480px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.6);
}
.modal-qr-card {
  background: #fff;
  border-radius: 1.25rem;
  max-width: 380px;
  width: 100%;
  overflow: hidden;
}
.modal-politicas-header {
  padding: 1.5rem 1.15rem 1.15rem;
  background: linear-gradient(135deg, #0d1b3e, #1e3a8a);
  color: white;
  border-radius: 1.25rem 1.25rem 0 0;
  text-align: center;
}
.modal-politicas-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 0.6rem;
  font-size: 1.15rem;
  color: #38bdf8;
  box-shadow: 0 0 20px rgba(56, 189, 248, 0.4);
}
.modal-politicas-title { font-size: 1.05rem; font-weight: 800; margin: 0 0 0.2rem; }
.modal-politicas-subtitle { font-size: 0.75rem; color: #cbd5e1; margin: 0; }
.modal-politicas-body { padding: 1rem; }
.politica-item {
  display: flex;
  gap: 0.65rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  margin-bottom: 0.6rem;
  border: 1px solid #e2e8f0;
  transition: all 0.2s;
}
.politica-item:hover { border-color: #cbd5e1; transform: translateX(2px); }
.politica-icon {
  width: 38px;
  height: 38px;
  border-radius: 0.6rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}
.politica-titulo { font-size: 0.85rem; font-weight: 800; color: #0f172a; margin-bottom: 0.2rem; }
.politica-desc { font-size: 0.75rem; color: #475569; margin: 0; line-height: 1.45; }
.bg-purple-subtle { background: #e0e7ff; }
.bg-orange-subtle { background: #ffedd5; }
.bg-blue-subtle { background: #dbeafe; }
.text-purple { color: #7c3aed; }

.modal-pago-header,
.modal-datos-pago-header {
  padding: 1rem 1.15rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 1.25rem 1.25rem 0 0;
}
.modal-pago-header { background: linear-gradient(135deg, #7c3aed, #6d28d9); }
.modal-datos-pago-header { background: linear-gradient(135deg, #25d366, #128c7e); }
.modal-qr-header {
  padding: 1rem 1.15rem;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #10b981, #059669);
}
.modal-pago-title,
.modal-datos-pago-title,
.modal-qr-title {
  font-size: 1rem;
  font-weight: 800;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.modal-pago-close,
.modal-datos-pago-close,
.modal-qr-close {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}
.modal-pago-close:hover,
.modal-datos-pago-close:hover,
.modal-qr-close:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: rotate(90deg);
}
.modal-pago-body,
.modal-datos-pago-body { padding: 1rem; }
.banco-btn {
  width: 100%;
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.75rem 0.4rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  min-height: 90px;
}
.banco-btn:hover {
  border-color: #7c3aed;
  transform: translateY(-3px);
  background: #faf5ff;
  box-shadow: 0 10px 20px -8px rgba(124, 58, 237, 0.4);
}
.banco-logo-img { width: 100%; max-width: 110px; height: 60px; object-fit: contain; }
.banco-info-card {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 0.85rem;
  padding: 0.85rem;
}
.banco-logo-lg-img {
  width: 55px;
  height: 42px;
  object-fit: contain;
  border-radius: 0.55rem;
  margin-right: 0.7rem;
  background: white;
  padding: 2px;
  border: 1px solid #e2e8f0;
  flex-shrink: 0;
}
.dato-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px dashed #e2e8f0;
  gap: 0.4rem;
  flex-wrap: wrap;
}
.dato-row:last-of-type { border-bottom: none; }
.dato-label { font-size: 0.68rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
.dato-valor { font-size: 0.82rem; font-weight: 800; color: #0f172a; word-break: break-word; }
.font-mono { font-family: 'SFMono-Regular', Consolas, monospace; }
.monto-row {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  margin: 0.4rem -0.3rem;
  padding: 0.65rem 0.55rem;
  border-radius: 0.65rem;
  border: 1px solid #a7f3d0;
  border-bottom: none;
}
.btn-copiar {
  background: linear-gradient(135deg, #0ea5e9, #0284c7);
  border: none;
  color: white;
  border-radius: 0.65rem;
  font-weight: 700;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 6px 14px -3px rgba(14, 165, 233, 0.45);
  transition: all 0.2s;
}
.btn-copiar:hover { transform: translateY(-2px); color: white; }
.btn-qr {
  background: linear-gradient(135deg, #10b981, #059669);
  border: none;
  color: white;
  border-radius: 0.65rem;
  font-weight: 700;
  padding: 0.6rem 0.9rem;
  font-size: 0.85rem;
  cursor: pointer;
  box-shadow: 0 6px 14px -3px rgba(16, 185, 129, 0.45);
  transition: all 0.2s;
}
.btn-qr:hover { transform: translateY(-2px); color: white; }
.btn-ya-pague {
  background: linear-gradient(135deg, #25d366, #128c7e);
  border: none;
  color: white;
  border-radius: 0.75rem;
  font-weight: 800;
  font-size: 0.88rem;
  padding: 0.8rem 1rem;
  box-shadow: 0 10px 20px -5px rgba(37, 211, 102, 0.45);
  cursor: pointer;
  transition: all 0.2s;
}
.btn-ya-pague:hover { transform: translateY(-2px); color: white; }
.btn-registrar-sistema {
  background: linear-gradient(135deg, #059669, #10b981);
  border: none;
  color: white;
  border-radius: 0.75rem;
  font-weight: 800;
  box-shadow: 0 10px 20px -5px rgba(16, 185, 129, 0.45);
  cursor: pointer;
  transition: all 0.2s;
}
.btn-registrar-sistema:hover:not(:disabled) {
  transform: translateY(-2px);
  color: white;
  box-shadow: 0 14px 24px -5px rgba(16, 185, 129, 0.6);
}
.btn-registrar-sistema:disabled { opacity: 0.5; cursor: not-allowed; }
.alert-proceso-info {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  border-radius: 0.65rem;
  padding: 0.65rem 0.8rem;
  font-size: 0.72rem;
  line-height: 1.4;
  display: flex;
  gap: 0.4rem;
}
.alert-info-pago {
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  border-radius: 0.65rem;
  padding: 0.65rem 0.8rem;
  font-size: 0.75rem;
  line-height: 1.4;
}
.label-form-pago {
  font-size: 0.65rem;
  font-weight: 800;
  color: #475569;
  margin-bottom: 0.35rem;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  text-transform: uppercase;
}
.input-pago {
  display: block;
  width: 100%;
  border-radius: 0.65rem !important;
  border: 2px solid #e2e8f0 !important;
  padding: 0.65rem 0.85rem !important;
  font-size: 0.85rem !important;
  font-weight: 600 !important;
  color: #0f172a !important;
  background: #f8fafc !important;
  transition: all 0.2s;
}
.input-pago:focus {
  border-color: #25d366 !important;
  background: #fff !important;
  box-shadow: 0 0 0 3px rgba(37, 211, 102, 0.15) !important;
  outline: none;
}
.input-pago[readonly] {
  background: #f1f5f9 !important;
  color: #475569 !important;
  cursor: not-allowed;
}
.input-pago-select {
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%2325d366' stroke-width='3'%3e%3cpolyline points='6 9 12 15 18 9'/%3e%3c/svg%3e") !important;
  background-repeat: no-repeat !important;
  background-position: right 0.75rem center !important;
  background-size: 0.9em !important;
  padding-right: 2.2rem !important;
  cursor: pointer;
}
.modal-qr-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.qr-img-modal {
  max-width: 100%;
  width: 240px;
  border-radius: 0.85rem;
  border: 3px solid #10b981;
  padding: 0.4rem;
  background: white;
  box-shadow: 0 10px 25px -8px rgba(16, 185, 129, 0.4);
}
.btn-consultar {
  background: linear-gradient(135deg, #059669, #10b981);
  border: none;
  color: white;
  border-radius: 0.65rem;
  box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.4);
}

/* ═══════════════════════════════════════════════════════════
   TRANSICIONES
═══════════════════════════════════════════════════════════ */
.chat-slide-enter-active,
.chat-slide-leave-active { transition: all 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
.chat-slide-enter .asistente-window,
.chat-slide-leave-to .asistente-window {
  transform: translateY(30px) scale(0.95);
  opacity: 0;
}
.msg-in-enter-active,
.msg-in-leave-active { transition: all 0.3s ease; }
.msg-in-enter { opacity: 0; transform: translateY(10px); }
.msg-in-leave-to { opacity: 0; }
.fade-slide-enter-active,
.fade-slide-leave-active { transition: all 0.3s ease; }
.fade-slide-enter,
.fade-slide-leave-to { opacity: 0; transform: translateY(15px); }

/* ═══════════════════════════════════════════════════════════
   RESPONSIVE (TODOS LOS DISPOSITIVOS)
═══════════════════════════════════════════════════════════ */
@media (max-width: 768px) {
  .jeanbot-floating-container {
    bottom: 34px;
    right: 18px;
    gap: 10px;
  }
  .jeanbot-mascot-wrapper {
    width: 108px;
    height: 128px;
  }
  .jeanbot-mascot-img {
    height: 122px;
    max-width: 112px;
  }
  .mascot-ground-shadow {
    width: 72px;
    height: 12px;
  }
  .jeanbot-welcome-card {
    max-width: 280px;
    padding: 12px 14px 10px 14px;
  }
  .welcome-title { font-size: 0.84rem; }
  .welcome-message { font-size: 0.74rem; }
  .welcome-cta-btn { font-size: 0.7rem; padding: 4px 10px; }
  .asistente-overlay {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
  .asistente-window {
    max-width: 100%;
    width: 100%;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    margin: 0;
  }
  .asistente-header { padding: 0.75rem 0.9rem; }
  .bot-avatar { width: 40px; height: 40px; }
  .bot-name { font-size: 0.9rem; }
  .bot-status { font-size: 0.68rem; }
  .header-btn { width: 32px; height: 32px; font-size: 0.8rem; }
  .asistente-body { padding: 0.85rem; }
  .msg-bubble { font-size: 0.82rem; max-width: 85%; }
  .msg-avatar-bot { width: 28px; height: 28px; }
}

@media (max-width: 520px) {
  .jeanbot-floating-container {
    bottom: calc(52px + env(safe-area-inset-bottom, 12px));
    right: 14px;
    flex-direction: column;
    align-items: flex-end;
    gap: 8px;
  }
  .jeanbot-mascot-wrapper {
    width: 98px;
    height: 118px;
  }
  .jeanbot-mascot-img {
    height: 112px;
    max-width: 102px;
  }
  .mascot-ground-shadow {
    width: 68px;
    height: 11px;
    bottom: 2px;
  }
  .mascot-online-pill {
    bottom: 8px;
    right: 0;
    padding: 2px 7px;
  }
  .mascot-online-label {
    font-size: 0.62rem;
  }
  .jeanbot-welcome-card {
    max-width: 270px;
    margin-bottom: 4px;
    padding: 12px 14px 10px 14px;
    border-radius: 16px;
  }
  .welcome-title { font-size: 0.82rem; }
  .welcome-message { font-size: 0.73rem; margin: 6px 0 8px 0; }
  .welcome-cta-btn { font-size: 0.7rem; padding: 5px 11px; }
  .welcome-bubble-pointer {
    right: 32px;
    bottom: -9px;
    border-left: 9px solid transparent;
    border-right: 9px solid transparent;
    border-top: 9px solid #ffffff;
    border-bottom: none;
    filter: drop-shadow(0 2px 2px rgba(0, 0, 0, 0.08));
  }
  .asistente-header { padding: 0.7rem 0.85rem; }
  .bot-avatar { width: 36px; height: 36px; }
  .bot-name { font-size: 0.86rem; }
  .bot-status { font-size: 0.66rem; }
  .header-btn { width: 30px; height: 30px; font-size: 0.78rem; }
  .header-actions { gap: 0.3rem; }
  .asistente-body { padding: 0.75rem; gap: 0.45rem; }
  .msg-bubble { font-size: 0.8rem; padding: 0.55rem 0.75rem; max-width: 88%; }
  .msg-avatar-bot { width: 26px; height: 26px; }
  .msg-hora { font-size: 0.58rem; }
  .input-chat-group { padding: 0.2rem 0.2rem 0.2rem 0.85rem; }
  .chat-input { font-size: 0.85rem; padding: 0.6rem 0.2rem; }
  .btn-send { width: 38px; height: 38px; font-size: 0.88rem; }
  .input-icon { font-size: 0.82rem; margin-right: 0.4rem; }
  .btn-opcion-chip { font-size: 0.72rem; padding: 0.35rem 0.7rem; }
}

@media (max-width: 380px) {
  .jeanbot-floating-container {
    bottom: calc(45px + env(safe-area-inset-bottom, 10px));
    right: 10px;
    gap: 6px;
  }
  .jeanbot-mascot-wrapper {
    width: 88px;
    height: 106px;
  }
  .jeanbot-mascot-img {
    height: 100px;
    max-width: 90px;
  }
  .jeanbot-welcome-card {
    max-width: 240px;
    padding: 10px 12px;
  }
  .welcome-title { font-size: 0.76rem; }
  .welcome-message { font-size: 0.68rem; }
}
</style>